import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import Sidebar from "@/components/Sidebar";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { TID } from "@/lib/testIds";
import { Clock, ShieldCheck, Sun, Moon } from "lucide-react";

const BREADCRUMBS = {
  "/app": ["Dashboard"],
  "/app/leads": ["Lead → Preventivo → PRD"],
  "/app/quotes": ["Preventivi"],
  "/app/prds": ["PRD"],
  "/app/clients": ["Clienti / Progetti"],
  "/app/agents": ["Agenti / Flussi"],
  "/app/memograph": ["Memoria (Memograph)"],
  "/app/governance": ["Governance / Stato"],
  "/app/integrations": ["Sistema", "Integrazioni"],
  "/app/settings": ["Sistema", "Impostazioni"],
};

function crumbsFor(pathname) {
  if (BREADCRUMBS[pathname]) return BREADCRUMBS[pathname];
  // Detail routes
  if (pathname.startsWith("/app/leads/")) return ["Lead → Preventivo → PRD", "Dettaglio Lead"];
  if (pathname.startsWith("/app/quotes/")) return ["Preventivi", "Dettaglio"];
  if (pathname.startsWith("/app/prds/")) return ["PRD", "Dettaglio"];
  if (pathname.startsWith("/app/clients/")) return ["Clienti / Progetti", "Dettaglio"];
  return ["Structura"];
}

export default function AppLayout() {
  const { session } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const crumbs = crumbsFor(location.pathname);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid={TID.appShell} className="min-h-screen flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <header
          data-testid={TID.topbar}
          className="border-b border-[#1F1F1F] px-6 py-3 flex items-center justify-between bg-[#0C0C0C] sticky top-0 z-10"
        >
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 flex items-center gap-2 truncate">
              structura://
              {crumbs.map((c, i) => (
                <span key={i} className={i === crumbs.length - 1 ? "text-neutral-300" : ""}>
                  {c}
                  {i < crumbs.length - 1 && <span className="mx-1 text-neutral-700">/</span>}
                </span>
              ))}
            </div>
            <h1 className="font-heading uppercase text-lg tracking-tight">
              {crumbs[crumbs.length - 1]}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Theme toggle · B/W */}
            <div
              data-testid={TID.themeToggle}
              className="flex border border-[#1F1F1F]"
              role="group"
              aria-label="Tema"
            >
              <button
                data-testid={TID.themeToggleLight}
                onClick={() => setTheme("light")}
                title="Tema chiaro"
                className={[
                  "px-2 py-1.5 transition-colors",
                  theme === "light"
                    ? "bg-[#EDEDED] text-black"
                    : "text-neutral-500 hover:text-neutral-300 hover:bg-[#141414]",
                ].join(" ")}
              >
                <Sun className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
              <button
                data-testid={TID.themeToggleDark}
                onClick={() => setTheme("dark")}
                title="Tema scuro"
                className={[
                  "px-2 py-1.5 border-l border-[#1F1F1F] transition-colors",
                  theme === "dark"
                    ? "bg-[#EDEDED] text-black"
                    : "text-neutral-500 hover:text-neutral-300 hover:bg-[#141414]",
                ].join(" ")}
              >
                <Moon className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="hidden lg:flex items-center gap-2 border border-[#1F1F1F] bg-[#050505] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-300">
              <ShieldCheck className="w-3 h-3 text-[#EDEDED]" strokeWidth={1.5} />
              lettura piena
            </div>
            <div className="font-mono text-[11px] text-neutral-500 hidden md:flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              {now.toISOString().replace("T", " ").slice(0, 19)}Z
            </div>
            <div
              data-testid={TID.topbarUser}
              className="font-mono text-xs text-neutral-300 border-l border-[#1F1F1F] pl-4"
            >
              {session?.email}
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
